import { Steps } from "antd";
import { Formik } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import ReactQuill from "react-quill-new";
import { MdDeleteOutline } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import GoogleMapInput from "../../components/GoogleMapInput";
import Input from "../../components/input";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import Select from "../../components/Select";
import {
  fetchBanksDropdown,
  fetchTransporterCitiesDropdown,
  fetchTransporterHeadquarterCitiesDropdown,
  fetchTransporterVehicleTypesDropdown,
} from "../../store/Slices/dropdownSlice";
import {
  createTransporter,
  fetchTransporterDetail,
  updateTransporter,
} from "../../store/Slices/TransporterSlice";
import "../../styles/TransporterSteps.css";
import { paymentTermsOptions } from "../../utils/Dropdowns";
import "../../styles/Trip.css";
import { formats, modules } from "../../utils/helper";
import { errorNotify, successNotify } from "../../utils/toast";
import { buildPhoneNumberSchema } from "./shared/phoneValidation";

const STATUS_ID_TO_KEY = {
  1: "active",
  2: "inactive",
  3: "prospect",
  4: "draft",
};

const toText = (value) => String(value ?? "").trim();
const resolveCoordinateText = (...values) => values.map(toText).find(Boolean) || "";
const resolveStatus = (status) => {
  const statusNumber = Number(status);
  if (Number.isFinite(statusNumber) && STATUS_ID_TO_KEY[statusNumber]) {
    return STATUS_ID_TO_KEY[statusNumber];
  }
  return normalize(status);
};

const resolveVehicleTypeValue = (vehicleType) =>
  toText(
    vehicleType?.title ??
    vehicleType?.name ??
    vehicleType?.label ??
    vehicleType?.slug ??
    vehicleType
  );

const resolveServiceValue = (service) =>
  toText(service?.service_title ?? service?.title ?? service);

const resolveOptionValue = (options, value, fallback = "") => {
  const selected = normalize(value);
  if (!selected) return fallback;

  const matched = safeArray(options).find(
    (option) =>
      normalize(option?.value) === selected || normalize(option?.label) === selected
  );

  return matched?.value ?? fallback;
};

const resolveOptionLabel = (options, value, fallback = "") => {
  const selected = normalize(value);
  if (!selected) return fallback;

  const matched = safeArray(options).find(
    (option) =>
      normalize(option?.value) === selected ||
      normalize(option?.id) === selected ||
      normalize(option?.label) === selected ||
      normalize(option?.name) === selected ||
      normalize(option?.title) === selected
  );

  return toText(
    matched?.label ??
    matched?.name ??
    matched?.title ??
    matched?.bank_name ??
    matched?.value ??
    fallback
  );
};

const cityOptions = [
  { label: "Islamabad", value: "islamabad" },
  { label: "Rawalpindi", value: "rawalpindi" },
  { label: "Lahore", value: "lahore" },
  { label: "Karachi", value: "karachi" },
  { label: "Faisalabad", value: "faisalabad" },
  { label: "Multan", value: "multan" },
  { label: "Peshawar", value: "peshawar" },
  { label: "Sialkot", value: "sialkot" },
];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Prospect", value: "prospect" },
];

const vehicleTypeOptions = [
  { label: "Light", value: 1 },
  { label: "Heavy", value: 2 },
  { label: "Trailers", value: 3 },
];

const supportedServiceOptions = [
  { label: "Loading", value: "loading" },
  { label: "Unloading", value: "unloading" },
];

const designationOptions = [
  { label: "Fleet Supervisor", value: "fleet_supervisor" },
  { label: "Transport Manager", value: "transport_manager" },
  { label: "Operations Executive", value: "operations_executive" },
];

const billingFrequencyOptions = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
];

const paymentMethodOptions = [
  { label: "Bank Transfer", value: "bank_transfer" },
  { label: "Cash", value: "cash" },
];

// Removed local paymentTermOptions to use shared paymentTermsOptions

const fallbackBankOptions = [
  { label: "AL-HABIB BANK", value: "al_habib_bank" },
  { label: "HBL", value: "hbl" },
  { label: "UBL", value: "ubl" },
];

const stepItems = [
  { title: "General Details" },
  { title: "Point of Contact" },
  { title: "Branches Details" },
  { title: "Attachments" },
  { title: "Payment Mode" },
];

const defaultNotes = "";
let tempIdCounter = 1;
const createTempId = () => Date.now() + tempIdCounter++;

const createSecondary = () => ({
  id: createTempId(),
  name: "",
  contact: "",
  designation: "",
  email: "",
});

const createBranch = () => ({
  id: createTempId(),
  branchName: "",
  city: "",
  contact: "",
  fleetSize: "",
  contactPersonName: "",
  contactPersonNumber: "",
  branchAddress: "",
  vehicleTypesSupported: [],
  supportedServices: [],
});

const defaultValues = () => ({
  transporterId: "",
  transporterName: "",
  contact: "",
  email: "",
  website: "",
  headquarter: "",
  address: "",
  fleetSize: "",
  status: "",
  vehicleTypesSupported: [],
  supportedServices: [],
  primaryContact: { name: "", contact: "", designation: "", email: "" },
  secondaryContacts: [],
  noBranches: false,
  branches: [],
  attachments: [],
  payment: {
    billingFrequency: "",
    paymentMethod: "",
    paymentTerm: "",
    bank: "",
    accountTitle: "",
    iban: "",
  },
  notes: defaultNotes,
});

const stepSchemas = [
  Yup.object().shape({
    transporterName: Yup.string().trim().required("Transporter Name is required"),
    contact: buildPhoneNumberSchema("Contact is required"),
    email: Yup.string()
      .trim()
      .nullable()
      .transform((v, o) => (String(o || "").trim() ? v : null))
      .email("Invalid email address"),
    headquarter: Yup.string().required("Headquarter is required"),
    address: Yup.string().trim().required("Address is required"),
    fleetSize: Yup.number()
      .typeError("Fleet Size must be a number")
      .min(1, "Fleet Size must be at least 1")
      .required("Fleet Size is required"),
    status: Yup.string().required("Status is required"),
    vehicleTypesSupported: Yup.array()
      .of(Yup.mixed())
      .min(1, "At least one vehicle type is required"),
    supportedServices: Yup.array()
      .of(Yup.string())
      .min(1, "At least one service is required"),
  }),
  Yup.object().shape({
    primaryContact: Yup.object().shape({
      name: Yup.string().trim().required("Primary contact name is required"),
      contact: buildPhoneNumberSchema("Primary contact number is required"),
      email: Yup.string()
        .trim()
        .nullable()
        .transform((v, o) => (String(o || "").trim() ? v : null))
        .email("Invalid email address"),
    }),
    secondaryContacts: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().trim().required("Secondary contact name is required"),
        contact: buildPhoneNumberSchema("Secondary contact number is required"),
        email: Yup.string()
          .trim()
          .nullable()
          .transform((v, o) => (String(o || "").trim() ? v : null))
          .email("Invalid email address"),
      })
    ),
  }),
  Yup.object().shape({
    noBranches: Yup.boolean(),
    branches: Yup.array().when("noBranches", {
      is: true,
      then: () => Yup.array(),
      otherwise: () =>
        Yup.array()
          .of(
            Yup.object().shape({
              branchName: Yup.string().trim().required("Branch Name is required"),
              city: Yup.string().required("City is required"),
              contact: buildPhoneNumberSchema("Contact is required"),
              fleetSize: Yup.number()
                .typeError("Fleet Size must be a number")
                .min(1, "Fleet Size must be at least 1")
                .required("Fleet Size is required"),
              contactPersonName: Yup.string()
                .trim()
                .required("Contact Person Name is required"),
              contactPersonNumber: buildPhoneNumberSchema(
                "Contact Person Number is required"
              ),
              branchAddress: Yup.string()
                .trim()
                .required("Branch Address is required"),
            })
          )
          .min(1, "At least one branch is required"),
    }),
  }),
  Yup.object().shape({
    attachments: Yup.array(),
  }),
  Yup.object().shape({
    payment: Yup.object().shape({
      billingFrequency: Yup.string().required("Billing Frequency is required"),
      paymentMethod: Yup.string()
        .oneOf(["bank_transfer", "cash"], "Payment Method is required")
        .required("Payment Method is required"),
      paymentTerm: Yup.string().when("paymentMethod", {
        is: (method) => ["bank_transfer", "cash"].includes(method),
        then: (schema) => schema.required("Payment Term is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      bank: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("Bank is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      accountTitle: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("Account Title is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      iban: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("IBAN / Account Number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  }),
];

const clone = (value) => JSON.parse(JSON.stringify(value));
const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalize = (value) => String(value || "").trim().toLowerCase();
const unique = (value) => [...new Set(safeArray(value).filter(Boolean))];
const cleanText = (value) => String(value || "").trim();
const toSentenceCase = (value) => {
  const text = cleanText(value).replace(/_/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};
const stripHtml = (value) =>
  cleanText(
    String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
  );

const vehicleTypeTextToValueMap = new Map(
  safeArray(vehicleTypeOptions).flatMap((item) => {
    const labelKey = normalize(item?.label);
    const valueKey = normalize(item?.value);
    return [
      [labelKey, item?.value],
      [valueKey, item?.value],
    ];
  })
);

const resolveVehicleTypeId = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const directNumber = Number(value);
  if (Number.isFinite(directNumber) && directNumber > 0) return directNumber;
  const mapped = vehicleTypeTextToValueMap.get(normalize(value));
  const mappedNumber = Number(mapped);
  return Number.isFinite(mappedNumber) && mappedNumber > 0 ? mappedNumber : null;
};

const resolveUploadedFilePath = (file) => {
  if (typeof file === "string") return cleanText(file);
  if (!file || typeof file !== "object") return "";
  return cleanText(
    file?.file_path ||
    file?.fileName ||
    file?.file_name ||
    file?.path ||
    file?.url ||
    file?.file ||
    file?.image ||
    file?.originalName ||
    ""
  );
};

const touchedFromErrors = (errors) => {
  if (Array.isArray(errors)) return errors.map((item) => touchedFromErrors(item));
  if (errors && typeof errors === "object") {
    return Object.keys(errors).reduce((acc, key) => {
      acc[key] = touchedFromErrors(errors[key]);
      return acc;
    }, {});
  }
  return true;
};

const toFormValues = (record) => {
  const base = defaultValues();
  if (!record) return base;

  const data = record?.formData ? clone(record.formData) : {};
  return {
    ...base,
    ...data,
    transporterId: record.id || data.transporterId || "",
    transporterName: data.transporterName || record.name || "",
    contact: data.contact || record.contact || "",
    email: data.email || record.email || "",
    website: data.website || record.website || "",
    headquarter: data.headquarter || record.headquarter || "",
    address: data.address || record.address || "",
    fleetSize: data.fleetSize || record.fleetSize || "",
    status: data.status || record.status || "prospect",
    vehicleTypesSupported: safeArray(
      data.vehicleTypesSupported || record.vehicleTypesSupported
    ).length
      ? safeArray(data.vehicleTypesSupported || record.vehicleTypesSupported)
      : base.vehicleTypesSupported,
    supportedServices: safeArray(data.supportedServices).length
      ? safeArray(data.supportedServices)
      : base.supportedServices,
    primaryContact: {
      ...base.primaryContact,
      ...(data.primaryContact || {}),
      name: data.primaryContact?.name || record.primaryContactPerson || "",
    },
    secondaryContacts: safeArray(data.secondaryContacts),
    noBranches:
      typeof data.noBranches === "boolean"
        ? data.noBranches
        : safeArray(record.branchesCities).length === 0,
    branches: safeArray(data.branches),
    attachments: safeArray(data.attachments),
    payment: { ...base.payment, ...(data.payment || {}) },
    notes: data.notes || base.notes,
  };
};

const toCreateTransporterPayload = (values) => {
  const branches = values.noBranches ? [] : safeArray(values.branches);
  const vehicleTypeIds = unique(
    safeArray(values.vehicleTypesSupported)
      .map(resolveVehicleTypeId)
      .filter((id) => Number.isFinite(id))
  );
  const pocDetails = [
    {
      name: cleanText(values.primaryContact?.name),
      email: cleanText(values.primaryContact?.email),
      contact: cleanText(values.primaryContact?.contact),
      designation: cleanText(values.primaryContact?.designation),
      is_primary: true,
    },
    ...safeArray(values.secondaryContacts).map((item) => ({
      name: cleanText(item?.name),
      email: cleanText(item?.email),
      contact: cleanText(item?.contact),
      designation: cleanText(item?.designation),
      is_primary: false,
    })),
  ].filter((item) => item.name || item.email || item.contact || item.designation);
  const mappedBranches = branches.map((item) => ({
    branch_name: cleanText(item?.branchName),
    city: toSentenceCase(item?.city),
    contact: cleanText(item?.contact),
    fleet_size: Number(item?.fleetSize || 0),
    contact_person_name: cleanText(item?.contactPersonName),
    contact_person_number: cleanText(item?.contactPersonNumber),
    vehicle_types_ids: unique(
      safeArray(item?.vehicleTypesSupported)
        .map(resolveVehicleTypeId)
        .filter((id) => Number.isFinite(id))
    ),
    supported_services: unique(
      safeArray(item?.supportedServices).map((service) => cleanText(service))
    ),
  }));

  return {
    name: cleanText(values.transporterName),
    contact: cleanText(values.contact),
    email: cleanText(values.email),
    website: cleanText(values.website),
    headquarter: toSentenceCase(values.headquarter),
    address: cleanText(values.address),
    status: cleanText(values.status),
    fleet_size: Number(values.fleetSize || 0),
    has_branches: !values.noBranches && mappedBranches.length > 0,
    note: stripHtml(values.notes),
    vehicle_types_ids: vehicleTypeIds,
    supported_services: unique(
      safeArray(values.supportedServices).map((service) => cleanText(service))
    ),
    poc_details: pocDetails,
    files: unique(
      safeArray(values.attachments)
        .map(resolveUploadedFilePath)
        .filter(Boolean)
    ),
    branches: values.noBranches ? [] : mappedBranches,
    payment_method: cleanText(values.payment?.paymentMethod),
    payment_terms: cleanText(values.payment?.paymentTerm),
    billing_frequency: cleanText(values.payment?.billingFrequency),
    bank_name: cleanText(values.payment?.bank),
    account_title: cleanText(values.payment?.accountTitle),
    iban_number: cleanText(values.payment?.iban),
  };
};

const withAddressCoordinates = (payload, values) => ({
  ...payload,
  lat: toText(values.addressLatitude),
  lng: toText(values.addressLongitude),
  branches: safeArray(payload.branches).map((branch, index) => ({
    ...branch,
    address: toText(values.branches?.[index]?.branchAddress),
    lat: toText(values.branches?.[index]?.branchLatitude),
    lng: toText(values.branches?.[index]?.branchLongitude),
  })),
});

const withBankName = (payload, values, bankOptions) => ({
  ...payload,
  bank_name: resolveOptionLabel(
    bankOptions,
    values.payment?.bank,
    values.payment?.bank
  ),
});

const resolveAddressCoordinates = async (address) => {
  const normalizedAddress = cleanText(address);
  if (!normalizedAddress) return { lat: "", lng: "" };

  try {
    const results = await geocodeByAddress(normalizedAddress);
    const coordinates = await getLatLng(results[0]);

    return {
      lat: cleanText(coordinates?.lat),
      lng: cleanText(coordinates?.lng),
    };
  } catch (error) {
    return { lat: "", lng: "" };
  }
};

const withResolvedAddressCoordinates = async (values = {}) => {
  const existingLatitude = cleanText(values.addressLatitude);
  const existingLongitude = cleanText(values.addressLongitude);
  const generalCoordinates =
    existingLatitude && existingLongitude
      ? { lat: existingLatitude, lng: existingLongitude }
      : await resolveAddressCoordinates(values.address);

  const branches = await Promise.all(
    safeArray(values.branches).map(async (branch) => {
      const branchLatitude = cleanText(branch?.branchLatitude);
      const branchLongitude = cleanText(branch?.branchLongitude);

      if (branchLatitude && branchLongitude) {
        return branch;
      }

      const coordinates = await resolveAddressCoordinates(branch?.branchAddress);

      return {
        ...branch,
        branchLatitude: branchLatitude || coordinates.lat,
        branchLongitude: branchLongitude || coordinates.lng,
      };
    })
  );

  return {
    ...values,
    addressLatitude: existingLatitude || generalCoordinates.lat,
    addressLongitude: existingLongitude || generalCoordinates.lng,
    branches,
  };
};

const mapTransporterDetailToFormValues = (detail) => {
  const base = defaultValues();
  if (!detail || typeof detail !== "object") return base;

  const poc = safeArray(detail?.transporters_poc || detail?.poc_details || detail?.poc);
  const primaryPoc = poc.find((entry) => Boolean(entry?.is_primary)) || poc[0] || {};
  const secondaryContacts = poc
    .filter((entry) => !entry?.is_primary)
    .map((entry, index) => ({
      id: entry?.id || `secondary-${index}`,
      name: toText(entry?.name),
      contact: toText(entry?.contact),
      designation: toText(entry?.designation),
      email: toText(entry?.email),
    }));

  const topVehicleTypesSource = safeArray(
    detail?.vehicle_types_ids || detail?.vehicle_types
  );
  const topVehicleTypes = topVehicleTypesSource
    .map((vehicleType) => {
      const id = Number(vehicleType?.id ?? vehicleType?.vehicle_type_id ?? vehicleType);
      if (Number.isFinite(id) && id > 0) return id;
      return resolveVehicleTypeValue(vehicleType) || null;
    })
    .filter(Boolean);

  const topSupportedServices = safeArray(
    detail?.supported_services || detail?.supportedServices
  )
    .map((service) => {
      const resolved = resolveServiceValue(service);
      const mapped = resolveOptionValue(
        supportedServiceOptions,
        resolved,
        resolved
      );
      return mapped || null;
    })
    .filter(Boolean);

  const branches = safeArray(detail?.branches).map((branch, index) => {
    const branchVehicleTypesSource = safeArray(
      branch?.vehicle_types_ids || branch?.vehicle_types
    );
    const branchVehicleTypes = branchVehicleTypesSource
      .map((vehicleType) => {
        const id = Number(vehicleType?.id ?? vehicleType?.vehicle_type_id ?? vehicleType);
        if (Number.isFinite(id) && id > 0) return id;
        return resolveVehicleTypeValue(vehicleType) || null;
      })
      .filter(Boolean);

    const branchServices = safeArray(
      branch?.supported_services || branch?.supportedServices
    )
      .map((service) => {
        const resolved = resolveServiceValue(service);
        const mapped = resolveOptionValue(
          supportedServiceOptions,
          resolved,
          resolved
        );
        return mapped || null;
      })
      .filter(Boolean);

    return {
      id: branch?.id || `branch-${index}`,
      branchName: toText(branch?.branch_name ?? branch?.name),
      city: toText(branch?.city),
      contact: toText(branch?.contact),
      fleetSize: toText(branch?.fleet_size),
      contactPersonName: toText(branch?.contact_person_name),
      contactPersonNumber: toText(branch?.contact_person_number),
      branchAddress: toText(branch?.address ?? branch?.branch_address),
      branchLatitude: resolveCoordinateText(
        branch?.lat,
        branch?.latitude,
        branch?.branch_lat,
        branch?.branch_latitude
      ),
      branchLongitude: resolveCoordinateText(
        branch?.lng,
        branch?.longitude,
        branch?.branch_lng,
        branch?.branch_longitude
      ),
      vehicleTypesSupported: branchVehicleTypes,
      supportedServices: branchServices,
    };
  });

  const status = resolveOptionValue(
    statusOptions,
    resolveStatus(detail?.status),
    "prospect"
  );

  const paymentMethod = resolveOptionValue(
    paymentMethodOptions,
    detail?.payment_method,
    toText(detail?.payment_method)
  );

  const paymentTerm = resolveOptionValue(
    paymentTermsOptions,
    detail?.payment_terms,
    toText(detail?.payment_terms)
  );

  const billingFrequency = resolveOptionValue(
    billingFrequencyOptions,
    detail?.billing_frequency,
    toText(detail?.billing_frequency)
  );

  const transporterId = toText(detail?.transporter_id) || toText(detail?.id);

  return {
    ...base,
    transporterId,
    transporterName: toText(detail?.name),
    contact: toText(detail?.contact),
    email: toText(detail?.email),
    website: toText(detail?.website),
    headquarter: toText(detail?.headquarter),
    address: toText(detail?.address),
    addressLatitude: resolveCoordinateText(
      detail?.lat,
      detail?.latitude,
      detail?.address_lat,
      detail?.address_latitude
    ),
    addressLongitude: resolveCoordinateText(
      detail?.lng,
      detail?.longitude,
      detail?.address_lng,
      detail?.address_longitude
    ),
    fleetSize: toText(detail?.fleet_size),
    status,
    vehicleTypesSupported: topVehicleTypes,
    supportedServices: topSupportedServices,
    primaryContact: {
      ...base.primaryContact,
      name: toText(primaryPoc?.name),
      contact: toText(primaryPoc?.contact),
      designation: toText(primaryPoc?.designation),
      email: toText(primaryPoc?.email),
    },
    secondaryContacts,
    noBranches: !(detail?.has_branches && branches.length > 0),
    branches,
    attachments: safeArray(detail?.files),
    payment: {
      ...base.payment,
      billingFrequency,
      paymentMethod,
      paymentTerm,
      bank: toText(detail?.bank_name),
      accountTitle: toText(detail?.account_title),
      iban: toText(detail?.iban_number),
    },
    notes: toText(detail?.note),
  };
};

const AddTransporter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeTransporterId } = useParams();
  const dispatch = useDispatch();

  const {
    banksDropdownData = [],
    transporterHeadquarterCitiesDropdownData = [],
    transporterCitiesDropdownData = [],
    transporterVehicleTypesDropdownData = [],
    loading: dropdownLoading,
  } = useSelector((state) => state.dropdowns);
  const {
    createTransporterLoading,
    updateTransporterLoading,
    transporterDetailLoading,
  } = useSelector((state) => state.transporter);

  const isEditMode = Boolean(routeTransporterId) || location.state?.mode === "edit";
  const editRecordFromState = location.state?.record || null;
  const editRecordId =
    routeTransporterId ||
    location.state?.id ||
    editRecordFromState?.recordId ||
    editRecordFromState?.id ||
    null;

  const [currentStep, setCurrentStep] = useState(0);
  const [initialValues, setInitialValues] = useState(defaultValues());
  const [detailLoading, setDetailLoading] = useState(Boolean(isEditMode && editRecordId));

  useEffect(() => {
    dispatch(fetchBanksDropdown());
    dispatch(fetchTransporterHeadquarterCitiesDropdown());
    dispatch(fetchTransporterCitiesDropdown());
    dispatch(fetchTransporterVehicleTypesDropdown());
  }, [dispatch]);

  useEffect(() => {
    let ignore = false;

    const loadInitialValues = async () => {
      if (isEditMode && editRecordId) {
        setDetailLoading(true);
      }

      let nextValues =
        isEditMode && editRecordFromState
          ? toFormValues(editRecordFromState)
          : defaultValues();

      if (isEditMode && editRecordId) {
        try {
          const response = await dispatch(fetchTransporterDetail(editRecordId)).unwrap();
          const detail = response?.data || response || null;
          if (detail) {
            nextValues = mapTransporterDetailToFormValues(detail);
          }
        } catch (error) {
          if (!editRecordFromState) {
            errorNotify(error || "Failed to fetch transporter detail");
          }
        }
      }

      if (!ignore) {
        setInitialValues(nextValues);
        setCurrentStep(0);
        setDetailLoading(false);
      }
    };

    loadInitialValues();

    return () => {
      ignore = true;
    };
  }, [dispatch, isEditMode, editRecordFromState, editRecordId]);

  const bankOptions = useMemo(
    () => (banksDropdownData?.length ? banksDropdownData : fallbackBankOptions),
    [banksDropdownData]
  );

  const getOptionLabel = (item) =>
    String(
      item?.label ?? item?.name ?? item?.title ?? item?.city ?? item?.value ?? ""
    ).trim();

  const headquarterCityOptions = useMemo(() => {
    const source = transporterHeadquarterCitiesDropdownData?.length
      ? transporterHeadquarterCitiesDropdownData
      : cityOptions;
    return source
      .map((item) => {
        const label = getOptionLabel(item);
        if (!label) return null;
        return { label, value: label };
      })
      .filter(Boolean);
  }, [transporterHeadquarterCitiesDropdownData]);

  const branchCityOptions = useMemo(() => {
    const source = transporterCitiesDropdownData?.length
      ? transporterCitiesDropdownData
      : cityOptions;
    return source
      .map((item) => {
        const label = getOptionLabel(item);
        if (!label) return null;
        return { label, value: label };
      })
      .filter(Boolean);
  }, [transporterCitiesDropdownData]);

  const vehicleTypeDropdownOptions = useMemo(() => {
    const source = transporterVehicleTypesDropdownData?.length
      ? transporterVehicleTypesDropdownData
      : vehicleTypeOptions;
    return source
      .map((item) => {
        const label = getOptionLabel(item);
        if (!label) return null;
        const rawValue = item?.value ?? item?.id ?? item?.vehicle_type_id;
        const numericValue = Number(rawValue);
        const value =
          Number.isFinite(numericValue) && numericValue > 0
            ? numericValue
            : rawValue;
        return value !== undefined && value !== null && value !== ""
          ? { label, value }
          : null;
      })
      .filter(Boolean);
  }, [transporterVehicleTypesDropdownData]);

  const pickOption = (options, selectedValue) => {
    const selected = String(selectedValue || "");
    return (
      options.find(
        (option) =>
          normalize(option?.value) === normalize(selected) ||
          normalize(option?.label) === normalize(selected)
      ) || null
    );
  };

  const pickMultiOptions = (options, selectedValues) =>
    options.filter((option) =>
      safeArray(selectedValues).some(
        (value) =>
          normalize(value) === normalize(option?.value) ||
          normalize(value) === normalize(option?.label)
      )
    );

  const fullValidationSchema = useMemo(
    () => stepSchemas.reduce((schema, stepSchema) => schema.concat(stepSchema)),
    []
  );

  const handleSave = async (values, { setSubmitting }) => {
    try {
      const valuesWithCoordinates = await withResolvedAddressCoordinates(values);

      if (isEditMode) {
        const targetId = Number(editRecordId || values.transporterId);
        if (!Number.isFinite(targetId)) {
          errorNotify("Invalid transporter id");
          return;
        }

        const payload = {
          id: targetId,
          ...withBankName(
            withAddressCoordinates(
              toCreateTransporterPayload(valuesWithCoordinates),
              valuesWithCoordinates
            ),
            valuesWithCoordinates,
            bankOptions
          ),
        };
        await dispatch(updateTransporter(payload)).unwrap();
        successNotify("Transporter updated successfully");
        navigate("/transporters");
        return;
      }

      const payload = withBankName(
        withAddressCoordinates(
          toCreateTransporterPayload(valuesWithCoordinates),
          valuesWithCoordinates
        ),
        valuesWithCoordinates,
        bankOptions
      );
      await dispatch(createTransporter(payload)).unwrap();
      successNotify("Transporter created successfully");
      navigate("/transporters");
    } catch (error) {
      errorNotify(error || "Failed to save transporter");
    } finally {
      setSubmitting(false);
    }
  };

  if (isEditMode && (detailLoading || transporterDetailLoading)) {
    return (
      <>
        <Breadcrumbs values={["Transporters", "Detail Transporter"]} />
        <div className="page_header access_view">
          <h2>Detail Transporter</h2>
        </div>
        <div className="loading_spinner">
          <Spinner animation="grow" />
        </div>
      </>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={isEditMode ? fullValidationSchema : stepSchemas[currentStep]}
      enableReinitialize
      onSubmit={handleSave}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        validateForm,
        setTouched,
        isSubmitting,
      }) => {
        const isLastStep = currentStep === stepItems.length - 1;
        const hasSecondaryContacts = safeArray(values.secondaryContacts).length > 0;
        const deleteActionStyle = {
          border: "none",
          background: "transparent",
          color: "#EF0000",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: 0,
          fontSize: 15,
          fontWeight: 400,
          marginTop: 2,
        };
        const addActionBaseStyle = {
          width: "100%",
          borderRadius: 10,
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          fontSize: 13,
          fontWeight: 400,
        };
        const getAddActionStyle = (disabled = false, extraStyles = {}) => ({
          ...addActionBaseStyle,
          border: disabled ? "1px solid #BDBDBD" : "1px solid #D3DEEE",
          background: disabled ? "#BDBDBD" : "#F4F7FD",
          color: disabled ? "#F2F2F2" : "#1447B1",
          cursor: disabled ? "not-allowed" : "pointer",
          ...extraStyles,
        });
        const requiredStar = (
          <span style={{ color: "red", fontSize: 13 }}>*</span>
        );
        const requiredLabel = (text, isRequired = true) =>
          isRequired ? (
            <>
              {text} {requiredStar}
            </>
          ) : (
            text
          );
        const sectionHeadingStyle = {
          background: "transparent",
          padding: 0,
          marginBottom: 12,
          color: "#3D3F5C",
          fontSize: 18,
          fontWeight: 600,
        };
        const sectionCardStyle = {
          background: "#FFFFFF",
          border: "1px solid #DDE3F0",
          borderRadius: 12,
          padding: "16px 14px",
          marginBottom: 14,
        };
        const setDigitsOnly = (fieldName) => (event) => {
          const digits = String(event?.target?.value ?? "").replace(/\D/g, "");
          setFieldValue(fieldName, digits);
        };

        const nextStep = async () => {
          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            return;
          }
          setCurrentStep((prev) => Math.min(prev + 1, stepItems.length - 1));
        };

        const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

        const gotoStep = async (target) => {
          if (target <= currentStep) {
            setCurrentStep(target);
            return;
          }

          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            return;
          }
          setCurrentStep(target);
        };

        const addSecondary = () => {
          setFieldValue("secondaryContacts", [
            ...safeArray(values.secondaryContacts),
            createSecondary(),
          ]);
        };

        const removeSecondary = (id) => {
          const nextItems = safeArray(values.secondaryContacts).filter(
            (item) => item.id !== id
          );
          setFieldValue("secondaryContacts", nextItems);
        };

        const addBranchRow = () => {
          setFieldValue("branches", [...safeArray(values.branches), createBranch()]);
        };

        const removeBranchRow = (id) => {
          const nextItems = safeArray(values.branches).filter(
            (item) => item.id !== id
          );
          setFieldValue("branches", nextItems);
        };

        return (
          <>
            <Breadcrumbs
              values={[
                "Transporters",
                isEditMode ? "Detail Transporter" : "Add Transporter",
              ]}
            />

            <div className="page_header access_view">
              <h2>{isEditMode ? "Detail Transporter" : "Add Transporter"}</h2>
              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate("/transporters")}
                  shouldRow={false}
                  isShowSubmitBtn={false}
                  cancelBtnText="Cancel"
                />
                <Button
                  submitText="Save"
                  submitBtn={handleSubmit}
                  loading={
                    isSubmitting || createTransporterLoading || updateTransporterLoading
                  }
                  disabled={
                    (!isEditMode && !isLastStep) ||
                    isSubmitting ||
                    createTransporterLoading ||
                    updateTransporterLoading
                  }
                  shouldRow={false}
                  isShowCancel={false}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={9}>
                  <div className="trip_information_main_div">
                    <h5>Transporter Details</h5>
                    <div>
                      <h6>
                        Transporter ID: <span>{values.transporterId || "--"}</span>
                      </h6>
                      <div className="d-flex">
                        {!isEditMode && (
                          <div style={{ minWidth: "175px" }}>
                            <Steps
                              className="transporter_steps form_steps_vertical"
                              current={currentStep}
                              direction="vertical"
                              items={stepItems}
                              onChange={gotoStep}
                            />
                          </div>
                        )}
                        <div className="w-100 mb-3">
                          {(isEditMode || currentStep === 0) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>General Details</h6>
                              )}
                              <Row>
                                <Col md={6}>
                                  <Input
                                    label="Transporter Name"
                                    placeholder="Enter Name"
                                    name="transporterName"
                                    value={values.transporterName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.transporterName}
                                    touched={touched.transporterName}
                                  />
                                </Col>
                                <Col md={6}>
                                  <PhoneNumberInput
                                    label="Contact"
                                    name="contact"
                                    value={values.contact}
                                    setFieldValue={setFieldValue}
                                    onBlur={handleBlur}
                                    error={errors.contact}
                                    touched={touched.contact}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Email Address"
                                    placeholder="Enter Email"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.email}
                                    touched={touched.email}
                                    required={false}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Website"
                                    placeholder="Enter Website"
                                    name="website"
                                    value={values.website}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.website}
                                    touched={touched.website}
                                    required={false}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Headquarter")}
                                    placeholder="Select City"
                                    options={headquarterCityOptions}
                                    loading={dropdownLoading}
                                    value={pickOption(
                                      headquarterCityOptions,
                                      values.headquarter
                                    )}
                                    name="headquarter"
                                    onChange={(opt) =>
                                      setFieldValue("headquarter", opt ? opt.value : "")
                                    }
                                    onBlur={() => setFieldTouched("headquarter", true)}
                                    error={errors.headquarter}
                                    touched={touched.headquarter}
                                  />
                                </Col>
                                <Col md={6}>
                                  <GoogleMapInput
                                    label="Address"
                                    name="address"
                                    value={values.address}
                                    setFieldValue={setFieldValue}
                                    onChange={(address, latitude, longitude) => {
                                      setFieldTouched("address", false, false);
                                      setFieldValue("address", address || "");
                                      setFieldValue("addressLatitude", latitude || "");
                                      setFieldValue("addressLongitude", longitude || "");
                                    }}
                                    error={errors.address}
                                    touched={touched.address}
                                    storeAs="string"
                                    latitudeFieldName="addressLatitude"
                                    longitudeFieldName="addressLongitude"
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Fleet Size"
                                    placeholder="Enter Fleet count"
                                    type="tel"
                                    name="fleetSize"
                                    value={values.fleetSize}
                                    onChange={setDigitsOnly("fleetSize")}
                                    onBlur={handleBlur}
                                    error={errors.fleetSize}
                                    touched={touched.fleetSize}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Status")}
                                    placeholder="Select Status"
                                    options={statusOptions}
                                    value={
                                      statusOptions.find(
                                        (opt) => opt.value === values.status
                                      ) || null
                                    }
                                    name="status"
                                    onChange={(opt) =>
                                      setFieldValue("status", opt ? opt.value : "")
                                    }
                                    onBlur={() => setFieldTouched("status", true)}
                                    error={errors.status}
                                    touched={touched.status}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Vehicle Types Supported")}
                                    placeholder="Select Vehicle Types"
                                    options={vehicleTypeDropdownOptions}
                                    loading={dropdownLoading}
                                    value={pickMultiOptions(
                                      vehicleTypeDropdownOptions,
                                      values.vehicleTypesSupported
                                    )}
                                    isMulti
                                    name="vehicleTypesSupported"
                                    onChange={(opts) =>
                                      setFieldValue(
                                        "vehicleTypesSupported",
                                        opts ? opts.map((opt) => opt.value) : []
                                      )
                                    }
                                    onBlur={() =>
                                      setFieldTouched("vehicleTypesSupported", true)
                                    }
                                    error={errors.vehicleTypesSupported}
                                    touched={touched.vehicleTypesSupported}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Supported Services")}
                                    placeholder="Select Services"
                                    options={supportedServiceOptions}
                                    value={pickMultiOptions(
                                      supportedServiceOptions,
                                      values.supportedServices
                                    )}
                                    isMulti
                                    name="supportedServices"
                                    onChange={(opts) =>
                                      setFieldValue(
                                        "supportedServices",
                                        opts ? opts.map((opt) => opt.value) : []
                                      )
                                    }
                                    onBlur={() =>
                                      setFieldTouched("supportedServices", true)
                                    }
                                    error={errors.supportedServices}
                                    touched={touched.supportedServices}
                                  />
                                </Col>
                                {!isEditMode && (
                                  <Col md={12}>
                                    <div className="d-flex align-items-center gap-2">
                                      <button type="button" className="cancel_btn" disabled>
                                        Previous
                                      </button>
                                      <button
                                        type="button"
                                        className="main_btn"
                                        onClick={nextStep}
                                      >
                                        Next
                                      </button>
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </div>
                          )}

                          {(isEditMode || currentStep === 1) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>Point of Contact</h6>
                              )}
                              <h6
                                style={{
                                  background: "transparent",
                                  padding: 0,
                                  marginBottom: 10,
                                  color: "#3D3F5C",
                                  fontSize: 16,
                                  fontWeight: 600,
                                }}
                              >
                                Primary Point of Contact
                              </h6>
                              <Row>
                                <Col md={6}>
                                  <Input
                                    label="Name"
                                    placeholder="Enter Name"
                                    name="primaryContact.name"
                                    value={values.primaryContact.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.primaryContact?.name}
                                    touched={touched.primaryContact?.name}
                                  />
                                </Col>
                                <Col md={6}>
                                  <PhoneNumberInput
                                    label="Contact"
                                    name="primaryContact.contact"
                                    value={values.primaryContact.contact}
                                    setFieldValue={setFieldValue}
                                    onBlur={handleBlur}
                                    error={errors.primaryContact?.contact}
                                    touched={touched.primaryContact?.contact}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label="Designation"
                                    placeholder="Select Designation"
                                    options={designationOptions}
                                    value={
                                      designationOptions.find(
                                        (opt) =>
                                          opt.value ===
                                          values.primaryContact.designation
                                      ) || null
                                    }
                                    name="primaryContact.designation"
                                    onChange={(opt) =>
                                      setFieldValue(
                                        "primaryContact.designation",
                                        opt ? opt.value : ""
                                      )
                                    }
                                    onBlur={() =>
                                      setFieldTouched(
                                        "primaryContact.designation",
                                        true
                                      )
                                    }
                                    error={errors.primaryContact?.designation}
                                    touched={touched.primaryContact?.designation}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Email Address"
                                    placeholder="Enter Email"
                                    name="primaryContact.email"
                                    value={values.primaryContact.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.primaryContact?.email}
                                    touched={touched.primaryContact?.email}
                                    required={false}
                                  />
                                </Col>
                              </Row>

                              {hasSecondaryContacts && (
                                <div
                                  style={{
                                    borderTop: "1px solid #DDE3F0",
                                    marginTop: 16,
                                    marginBottom: 14,
                                  }}
                                />
                              )}

                              {safeArray(values.secondaryContacts).map((item, index) => (
                                <div key={item.id} style={{ marginBottom: 12 }}>
                                  <h6
                                    style={{
                                      background: "transparent",
                                      padding: 0,
                                      marginBottom: 10,
                                      color: "#3D3F5C",
                                      fontSize: 16,
                                      fontWeight: 600,
                                    }}
                                  >
                                    Secondary Point of Contact
                                  </h6>
                                  <Row>
                                    <Col md={6}>
                                      <Input
                                        label="Name"
                                        placeholder="Enter Name"
                                        name={`secondaryContacts[${index}].name`}
                                        value={item.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.secondaryContacts?.[index]?.name}
                                        touched={touched.secondaryContacts?.[index]?.name}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <PhoneNumberInput
                                        label="Contact"
                                        name={`secondaryContacts[${index}].contact`}
                                        value={item.contact}
                                        setFieldValue={setFieldValue}
                                        onBlur={handleBlur}
                                        error={errors.secondaryContacts?.[index]?.contact}
                                        touched={touched.secondaryContacts?.[index]?.contact}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label="Designation"
                                        placeholder="Select Designation"
                                        options={designationOptions}
                                        value={
                                          designationOptions.find(
                                            (opt) => opt.value === item.designation
                                          ) || null
                                        }
                                        name={`secondaryContacts[${index}].designation`}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `secondaryContacts[${index}].designation`,
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(
                                            `secondaryContacts[${index}].designation`,
                                            true
                                          )
                                        }
                                        error={errors.secondaryContacts?.[index]?.designation}
                                        touched={
                                          touched.secondaryContacts?.[index]?.designation
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Email Address"
                                        placeholder="Enter Email"
                                        name={`secondaryContacts[${index}].email`}
                                        value={item.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.secondaryContacts?.[index]?.email}
                                        touched={touched.secondaryContacts?.[index]?.email}
                                        required={false}
                                      />
                                    </Col>
                                  </Row>

                                  <button
                                    type="button"
                                    onClick={() => removeSecondary(item.id)}
                                    style={deleteActionStyle}
                                  >
                                    <MdDeleteOutline style={{ fontSize: 18 }} />
                                    Delete Contact Person
                                  </button>

                                  {index !== safeArray(values.secondaryContacts).length - 1 && (
                                    <div
                                      style={{
                                        borderTop: "1px solid #DDE3F0",
                                        marginTop: 14,
                                        marginBottom: 14,
                                      }}
                                    />
                                  )}
                                </div>
                              ))}

                              <button
                                type="button"
                                onClick={addSecondary}
                                style={getAddActionStyle(false, {
                                  marginTop: hasSecondaryContacts ? 6 : 12,
                                })}
                              >
                                <FiPlus style={{ fontSize: 20 }} />
                                Add Contact Person
                              </button>
                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-3">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="main_btn"
                                    onClick={nextStep}
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {(isEditMode || currentStep === 2) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>Branches Details</h6>
                              )}
                              <p
                                style={{
                                  color: "#3D3F5C",
                                  fontSize: 15,
                                  marginBottom: 10,
                                }}
                              >
                                Add transporter branches by city/location. If none,
                                check "No Branches Available".
                              </p>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <input
                                  id="noBranches"
                                  type="checkbox"
                                  checked={values.noBranches}
                                  onChange={(event) =>
                                    setFieldValue("noBranches", event.target.checked)
                                  }
                                  style={{
                                    width: 18,
                                    height: 18,
                                    accentColor: "#1447B1",
                                    cursor: "pointer",
                                  }}
                                />
                                <label
                                  htmlFor="noBranches"
                                  style={{
                                    marginBottom: 0,
                                    color: "#3D3F5C",
                                    fontSize: 15,
                                    fontWeight: 400,
                                    cursor: "pointer",
                                  }}
                                >
                                  No Branches Available
                                </label>
                              </div>

                              <div
                                style={{
                                  borderTop: "1px solid #DDE3F0",
                                  marginTop: 8,
                                  marginBottom: 18,
                                }}
                              />

                              {!values.noBranches &&
                                safeArray(values.branches).map((item, index) => (
                                  <div key={item.id} style={{ marginBottom: 12 }}>
                                    <Row>
                                      <Col md={6}>
                                        <Input
                                          label="Branch Name"
                                          placeholder="Enter Name"
                                          name={`branches[${index}].branchName`}
                                          value={item.branchName}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={errors.branches?.[index]?.branchName}
                                          touched={touched.branches?.[index]?.branchName}
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Select
                                          label={requiredLabel("City")}
                                          placeholder="Select City"
                                          options={branchCityOptions}
                                          loading={dropdownLoading}
                                          value={pickOption(branchCityOptions, item.city)}
                                          name={`branches[${index}].city`}
                                          onChange={(opt) =>
                                            setFieldValue(
                                              `branches[${index}].city`,
                                              opt ? opt.value : ""
                                            )
                                          }
                                          onBlur={() =>
                                            setFieldTouched(
                                              `branches[${index}].city`,
                                              true
                                            )
                                          }
                                          error={errors.branches?.[index]?.city}
                                          touched={touched.branches?.[index]?.city}
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <PhoneNumberInput
                                          label="Contact"
                                          name={`branches[${index}].contact`}
                                          value={item.contact}
                                          setFieldValue={setFieldValue}
                                          onBlur={handleBlur}
                                          error={errors.branches?.[index]?.contact}
                                          touched={touched.branches?.[index]?.contact}
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Input
                                          label="Fleet Size"
                                          placeholder="Enter Fleet count"
                                          type="tel"
                                          name={`branches[${index}].fleetSize`}
                                          value={item.fleetSize}
                                          onChange={setDigitsOnly(
                                            `branches[${index}].fleetSize`
                                          )}
                                          onBlur={handleBlur}
                                          error={errors.branches?.[index]?.fleetSize}
                                          touched={touched.branches?.[index]?.fleetSize}
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Input
                                          label="Contact Person Name"
                                          placeholder="Enter Contact Person Name"
                                          name={`branches[${index}].contactPersonName`}
                                          value={item.contactPersonName}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            errors.branches?.[index]?.contactPersonName
                                          }
                                          touched={
                                            touched.branches?.[index]?.contactPersonName
                                          }
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <PhoneNumberInput
                                          label="Contact Person Number"
                                          name={`branches[${index}].contactPersonNumber`}
                                          value={item.contactPersonNumber}
                                          setFieldValue={setFieldValue}
                                          onBlur={handleBlur}
                                          error={
                                            errors.branches?.[index]?.contactPersonNumber
                                          }
                                          touched={
                                            touched.branches?.[index]?.contactPersonNumber
                                          }
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Select
                                          label="Vehicle Types Supported"
                                          placeholder="Select Vehicle Types"
                                          options={vehicleTypeDropdownOptions}
                                          loading={dropdownLoading}
                                          value={pickMultiOptions(
                                            vehicleTypeDropdownOptions,
                                            item.vehicleTypesSupported
                                          )}
                                          isMulti
                                          name={`branches[${index}].vehicleTypesSupported`}
                                          onChange={(opts) =>
                                            setFieldValue(
                                              `branches[${index}].vehicleTypesSupported`,
                                              opts
                                                ? opts.map((opt) => opt.value)
                                                : []
                                            )
                                          }
                                          onBlur={() =>
                                            setFieldTouched(
                                              `branches[${index}].vehicleTypesSupported`,
                                              true
                                            )
                                          }
                                          error={
                                            errors.branches?.[index]
                                              ?.vehicleTypesSupported
                                          }
                                          touched={
                                            touched.branches?.[index]
                                              ?.vehicleTypesSupported
                                          }
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Select
                                          label="Supported Services"
                                          placeholder="Select Services"
                                          options={supportedServiceOptions}
                                          value={pickMultiOptions(
                                            supportedServiceOptions,
                                            item.supportedServices
                                          )}
                                          isMulti
                                          name={`branches[${index}].supportedServices`}
                                          onChange={(opts) =>
                                            setFieldValue(
                                              `branches[${index}].supportedServices`,
                                              opts
                                                ? opts.map((opt) => opt.value)
                                                : []
                                            )
                                          }
                                          onBlur={() =>
                                            setFieldTouched(
                                              `branches[${index}].supportedServices`,
                                              true
                                            )
                                          }
                                          error={
                                            errors.branches?.[index]
                                              ?.supportedServices
                                          }
                                          touched={
                                            touched.branches?.[index]
                                              ?.supportedServices
                                          }
                                        />
                                      </Col>
                                      <Col md={12}>
                                        <GoogleMapInput
                                          label="Branch Address"
                                          name={`branches[${index}].branchAddress`}
                                          value={item.branchAddress}
                                          setFieldValue={setFieldValue}
                                          onChange={(address, latitude, longitude) => {
                                            setFieldTouched(
                                              `branches[${index}].branchAddress`,
                                              false,
                                              false
                                            );
                                            setFieldValue(
                                              `branches[${index}].branchAddress`,
                                              address || ""
                                            );
                                            setFieldValue(
                                              `branches[${index}].branchLatitude`,
                                              latitude || ""
                                            );
                                            setFieldValue(
                                              `branches[${index}].branchLongitude`,
                                              longitude || ""
                                            );
                                          }}
                                          error={errors.branches?.[index]?.branchAddress}
                                          touched={touched.branches?.[index]?.branchAddress}
                                          storeAs="string"
                                          latitudeFieldName={`branches[${index}].branchLatitude`}
                                          longitudeFieldName={`branches[${index}].branchLongitude`}
                                        />
                                      </Col>
                                    </Row>

                                    <button
                                      type="button"
                                      onClick={() => removeBranchRow(item.id)}
                                      style={deleteActionStyle}
                                    >
                                      <MdDeleteOutline style={{ fontSize: 18 }} />
                                      Delete Transporter Branch
                                    </button>

                                    <div
                                      style={{
                                        borderTop: "1px solid #DDE3F0",
                                        marginTop: 14,
                                        marginBottom: 14,
                                      }}
                                    />
                                  </div>
                                ))}

                              <button
                                type="button"
                                onClick={addBranchRow}
                                disabled={values.noBranches}
                                style={getAddActionStyle(values.noBranches)}
                              >
                                <FiPlus style={{ fontSize: 20 }} />
                                Add Transporter Branch
                              </button>

                              {touched.branches &&
                                typeof errors.branches === "string" &&
                                !values.noBranches && (
                                  <div
                                    className="error-message"
                                    style={{
                                      color: "#ff0000de",
                                      fontSize: 12,
                                      marginTop: 4,
                                    }}
                                  >
                                    {errors.branches}
                                  </div>
                                )}

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-3">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="main_btn"
                                    onClick={nextStep}
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {(isEditMode || currentStep === 3) && (
                            <>
                              <div style={sectionCardStyle}>
                                {isEditMode && (
                                  <h6 style={sectionHeadingStyle}>Attachments</h6>
                                )}
                                <p
                                  style={{
                                    color: "#3D3F5C",
                                    fontSize: 15,
                                    marginBottom: 10,
                                  }}
                                >
                                  You can attach up to 5 files, max 1MB each.
                                </p>
                                <FileUpload
                                  destination="3pl-transporters"
                                  files={safeArray(values.attachments)}
                                  setFiles={(nextFiles) => {
                                    const resolved =
                                      typeof nextFiles === "function"
                                        ? nextFiles(safeArray(values.attachments))
                                        : nextFiles;
                                    setFieldValue("attachments", resolved);
                                  }}
                                  maxFiles={5}
                                  maxFileSize={1}
                                />
                                {touched.attachments && errors.attachments && (
                                  <div
                                    className="error-message"
                                    style={{
                                      color: "#ff0000de",
                                      fontSize: 12,
                                      marginTop: 4,
                                    }}
                                  >
                                    {errors.attachments}
                                  </div>
                                )}
                              </div>
                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-3">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="main_btn"
                                    onClick={nextStep}
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </>
                          )}

                          {(isEditMode || currentStep === 4) && (
                            <>
                              <div style={{ ...sectionCardStyle, marginBottom: 0 }}>
                                {isEditMode && (
                                  <h6 style={sectionHeadingStyle}>Payment Mode</h6>
                                )}
                                <Row>
                                  <Col md={6}>
                                    <Select
                                      label={requiredLabel("Billing Frequency")}
                                      placeholder="Select Frequency"
                                      options={billingFrequencyOptions}
                                      value={
                                        billingFrequencyOptions.find(
                                          (opt) =>
                                            opt.value ===
                                            values.payment.billingFrequency
                                        ) || null
                                      }
                                      name="payment.billingFrequency"
                                      onChange={(opt) =>
                                        setFieldValue(
                                          "payment.billingFrequency",
                                          opt ? opt.value : ""
                                        )
                                      }
                                      onBlur={() =>
                                        setFieldTouched("payment.billingFrequency", true)
                                      }
                                      error={errors.payment?.billingFrequency}
                                      touched={touched.payment?.billingFrequency}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Select
                                      label={requiredLabel("Payment Method")}
                                      placeholder="Select Method"
                                      options={paymentMethodOptions}
                                      value={
                                        paymentMethodOptions.find(
                                          (opt) =>
                                            opt.value === values.payment.paymentMethod
                                        ) || null
                                      }
                                      name="payment.paymentMethod"
                                      onChange={(opt) => {
                                        const method = opt ? opt.value : "";
                                        setFieldValue("payment.paymentMethod", method);

                                        if (!["bank_transfer", "cash"].includes(method)) {
                                          setFieldValue("payment.paymentTerm", "");
                                        }

                                        if (method !== "bank_transfer") {
                                          setFieldValue("payment.bank", "");
                                          setFieldValue("payment.accountTitle", "");
                                          setFieldValue("payment.iban", "");
                                        }
                                      }}
                                      onBlur={() =>
                                        setFieldTouched("payment.paymentMethod", true)
                                      }
                                      error={errors.payment?.paymentMethod}
                                      touched={touched.payment?.paymentMethod}
                                    />
                                  </Col>

                                  {["bank_transfer", "cash"].includes(
                                    values.payment.paymentMethod
                                  ) && (
                                      <Col md={6}>
                                        <Select
                                          label={requiredLabel("Payment Term")}
                                          placeholder="Select Payment Term"
                                          options={paymentTermsOptions}
                                          value={
                                            paymentTermsOptions.find(
                                              (opt) =>
                                                String(opt.value) === String(values.payment.paymentTerm)
                                            ) || null
                                          }
                                          name="payment.paymentTerm"
                                          onChange={(opt) =>
                                            setFieldValue(
                                              "payment.paymentTerm",
                                              opt ? opt.value : ""
                                            )
                                          }
                                          onBlur={() =>
                                            setFieldTouched("payment.paymentTerm", true)
                                          }
                                          error={errors.payment?.paymentTerm}
                                          touched={touched.payment?.paymentTerm}
                                        />
                                      </Col>
                                    )}

                                  {values.payment.paymentMethod === "bank_transfer" && (
                                    <>
                                      <Col md={6}>
                                        <Select
                                          label={requiredLabel("Bank")}
                                          placeholder="Select Bank"
                                          options={bankOptions}
                                          loading={dropdownLoading}
                                          value={pickOption(bankOptions, values.payment.bank)}
                                          name="payment.bank"
                                          onChange={(opt) =>
                                            setFieldValue(
                                              "payment.bank",
                                              opt ? opt.value : ""
                                            )
                                          }
                                          onBlur={() =>
                                            setFieldTouched("payment.bank", true)
                                          }
                                          error={errors.payment?.bank}
                                          touched={touched.payment?.bank}
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Input
                                          label="Account Title"
                                          placeholder="Enter Account Title"
                                          name="payment.accountTitle"
                                          value={values.payment.accountTitle}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={errors.payment?.accountTitle}
                                          touched={touched.payment?.accountTitle}
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Input
                                          label="IBAN / Account Number"
                                          placeholder="Enter IBAN / Account Number"
                                          name="payment.iban"
                                          value={values.payment.iban}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={errors.payment?.iban}
                                          touched={touched.payment?.iban}
                                        />
                                      </Col>
                                    </>
                                  )}
                                </Row>
                              </div>

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <div
                    className="trip_notes_main_div"
                    style={{ minHeight: "80vh", height: "100%" }}
                  >
                    <h5>Notes</h5>
                    <div style={{ padding: "0 12px 12px" }}>
                      <ReactQuill
                        placeholder="Type your notes or additional info..."
                        modules={modules}
                        theme="snow"
                        value={values.notes || ""}
                        formats={formats}
                        onChange={(content) => setFieldValue("notes", content)}
                        className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

export default AddTransporter;
